import { resolveApiPath } from "@rapid/utils/api/utils";
import pluralize from "pluralize";

type ExtractedObjectPair = { original: string; english: string };

/**
 * Calls backend to extract and translate object names from user input.
 * Returns pairs of {original, english} for each detected object.
 */
async function extractObjectsFromPromptWithAI(
    prompt: string,
    abortController?: AbortController
): Promise<ExtractedObjectPair[]> {
    try {
        const url = resolveApiPath("/api/extract-objects");

        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ prompt }),
            signal: abortController?.signal,
            credentials: "include"
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = "Object extraction failed";
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorJson.message || errorMessage;
            } catch {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const result = await response.json();
        return result.objects || [];
    } catch (error) {
        console.error("Failed to extract objects with AI:", error);
        throw error;
    }
}

export type ProcessedPromptResult = {
    processedInput: string;
    wasExtracted: boolean;
    originalInput?: string;
    extractedObjects?: string[];
    /** Maps user's original term → English translation. Only populated when translation occurred. */
    promptNames?: Map<string, string>;
};

/**
 * Trims, lowercases, and drops empty entries. Used for the user-facing original term where the
 * language is unknown — pluralize is English-only and would mangle non-English words. Lowercasing
 * keeps label keys consistent with PromptLabelAnnotateCard.handleSaveEdit, which also lowercases
 * on rename, so promptName lookups don't drift on case.
 */
function normalizeTerms(objects: string[]): string[] {
    return objects.map((obj) => obj.trim().toLowerCase()).filter((obj) => obj.length > 0);
}

/**
 * Singularizes a list of English object names. Only safe for English — pluralize doesn't
 * support other languages and may corrupt them (e.g. Turkish "arabalar" → "arabala").
 */
function singularizeEnglishTerms(objects: string[]): string[] {
    return objects.map((obj) => pluralize.singular(obj.trim())).filter((obj) => obj.length > 0);
}

/**
 * Processes user input: always sends to backend for extraction + translation.
 * Returns extracted objects with English terms for SAM3 and original terms for display names.
 */
export async function processTextPromptInputWithAI(
    input: string,
    abortController?: AbortController
): Promise<ProcessedPromptResult> {
    const trimmed = input.trim();

    if (!trimmed) {
        return { processedInput: "", wasExtracted: false };
    }

    const pairs = await extractObjectsFromPromptWithAI(trimmed, abortController);

    const originalTerms = normalizeTerms(pairs.map((p) => p.original));
    const englishTerms = singularizeEnglishTerms(pairs.map((p) => p.english));
    const promptNames = new Map<string, string>();

    for (let i = 0; i < originalTerms.length && i < englishTerms.length; i++) {
        if (originalTerms[i].toLowerCase() !== englishTerms[i].toLowerCase()) {
            promptNames.set(originalTerms[i], englishTerms[i]);
        }
    }

    return {
        processedInput: originalTerms.join(", "),
        wasExtracted: true,
        originalInput: trimmed,
        extractedObjects: originalTerms,
        promptNames: promptNames.size > 0 ? promptNames : undefined
    };
}
